import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, LinkedinLogo, EnvelopeSimple, User } from "@phosphor-icons/react";
import Header from "../sections/Header";
import Footer from "../sections/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Placeholder team data — editable via /admin/content (page.our-core-team JSON)
const PLACEHOLDER = {
  h1: "Our Core Team",
  hero_copy: "The people behind OpSys Digital — combining engineering depth, technology know-how and delivery focus to help clients build reliable, scalable and future-ready systems.",
  rows: [
    { layout: "solo", members: [{ name: "Sajid", designation: "Founder & CEO", bio: "Bio coming soon.", photo_id: null, linkedin: "", email: "sajid@opsysdigital.com" }] },
    { layout: "solo", members: [{ name: "Anjana", designation: "Director — Operations", bio: "Bio coming soon.", photo_id: null, linkedin: "", email: "" }] },
    { layout: "duo",  members: [
      { name: "Team Member 3", designation: "Designation", bio: "Bio coming soon.", photo_id: null, linkedin: "", email: "" },
      { name: "Team Member 4", designation: "Designation", bio: "Bio coming soon.", photo_id: null, linkedin: "", email: "" },
    ]},
  ],
};

function Avatar({ photoId, name }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!photoId) { setSrc(null); return; }
    let c = false;
    fetch(`${API}/images/${photoId}`).then((r) => r.json()).then((j) => !c && setSrc(j.data_url)).catch(() => {});
    return () => { c = true; };
  }, [photoId]);
  if (src) return <img src={src} alt={name} className="w-full h-full object-cover" />;
  return (
    <div className="w-full h-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #0F2A6E 0%, #1B6FE0 100%)" }}>
      <User size={48} weight="duotone" color="#FFFFFF" />
    </div>
  );
}

function SoloRow({ member, idx }) {
  const align = idx % 2 === 0;
  return (
    <div className="anim anim-up grid md:grid-cols-12 gap-8 md:gap-12 items-center py-10 md:py-14 border-b border-slate-100 last:border-b-0">
      <div className={`md:col-span-4 ${align ? "" : "md:order-2"}`}>
        <div className="relative aspect-[4/5] w-full max-w-[280px] mx-auto md:mx-0 rounded-2xl overflow-hidden shadow-lg" data-testid={`team-photo-${idx}`}>
          <Avatar photoId={member.photo_id} name={member.name} />
        </div>
      </div>
      <div className={`md:col-span-8 ${align ? "" : "md:order-1"}`}>
        <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-[42px] tracking-tight" style={{ color: "#0F2A6E", letterSpacing: "-0.025em" }} data-testid={`team-name-${idx}`}>
          {member.name}
        </h2>
        <div className="mt-2 text-[15px] font-semibold" style={{ color: "#1B6FE0" }} data-testid={`team-designation-${idx}`}>
          {member.designation}
        </div>
        {member.bio && (
          <p className="mt-5 text-[15px] text-[#1C2B3A] leading-[1.85] font-light max-w-2xl whitespace-pre-line">{member.bio}</p>
        )}
        <div className="mt-5 flex items-center gap-3">
          {member.linkedin && (
            <a href={member.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[13px] font-medium hover:opacity-70 transition" style={{ color: "#0F2A6E" }}>
              <LinkedinLogo size={16} weight="duotone" /> LinkedIn
            </a>
          )}
          {member.email && (
            <a href={`mailto:${member.email}`} className="inline-flex items-center gap-1.5 text-[13px] font-medium hover:opacity-70 transition" style={{ color: "#0F2A6E" }}>
              <EnvelopeSimple size={16} weight="duotone" /> {member.email}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function DuoRow({ members, idx }) {
  return (
    <div className="anim anim-up grid md:grid-cols-2 gap-8 md:gap-10 py-10 md:py-14 border-b border-slate-100 last:border-b-0">
      {members.map((m, i) => (
        <div key={i} className="text-center md:text-start" data-testid={`team-duo-${idx}-${i}`}>
          <div className="relative aspect-square w-40 md:w-56 mx-auto md:mx-0 rounded-2xl overflow-hidden shadow-md">
            <Avatar photoId={m.photo_id} name={m.name} />
          </div>
          <h3 className="mt-5 font-heading font-bold text-2xl tracking-tight" style={{ color: "#0F2A6E", letterSpacing: "-0.02em" }}>
            {m.name}
          </h3>
          <div className="mt-1 text-[14px] font-semibold" style={{ color: "#1B6FE0" }}>{m.designation}</div>
          {m.bio && <p className="mt-4 text-[14px] text-[#1C2B3A] leading-relaxed font-light">{m.bio}</p>}
          <div className="mt-4 flex items-center gap-3 justify-center md:justify-start">
            {m.linkedin && <a href={m.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[12.5px] font-medium hover:opacity-70" style={{ color: "#0F2A6E" }}><LinkedinLogo size={14} weight="duotone" /> LinkedIn</a>}
            {m.email && <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1.5 text-[12.5px] font-medium hover:opacity-70" style={{ color: "#0F2A6E" }}><EnvelopeSimple size={14} weight="duotone" /> {m.email}</a>}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function OurCoreTeam() {
  const [data, setData] = useState(null);
  useEffect(() => {
    document.title = "Our Core Team | OpSys Digital";
    axios.get(`${API}/pages/our-core-team`)
      .then((r) => setData(r.data.page))
      .catch(() => setData(PLACEHOLDER));
  }, []);

  const d = data || PLACEHOLDER;

  return (
    <>
      <Header />
      <main className="bg-white">
        {/* HERO */}
        <section className="relative overflow-hidden text-white py-20 md:py-28"
          style={{ background: "linear-gradient(135deg, #0B1F4D 0%, #0F2A6E 55%, #1B6FE0 100%)" }}>
          <div className="absolute inset-0 grid-bg opacity-12" />
          <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full blur-3xl blob-drift" style={{ background: "rgba(27,111,224,0.35)" }} />
          <div className="relative max-w-5xl mx-auto px-4 lg:px-8">
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-[54px] tracking-tight leading-[1.05]" style={{ letterSpacing: "-0.025em" }} data-testid="team-h1">
              {d.h1 || "Our Core Team"}
            </h1>
            {d.hero_copy && (
              <p className="mt-6 text-[14.5px] md:text-[17px] text-slate-200/90 leading-relaxed font-light max-w-3xl">{d.hero_copy}</p>
            )}
          </div>
        </section>

        {/* TEAM ROWS */}
        <section className="py-8 md:py-12">
          <div className="max-w-6xl mx-auto px-4 lg:px-8">
            {(d.rows || []).map((row, idx) =>
              row.layout === "duo"
                ? <DuoRow key={idx} members={row.members} idx={idx} />
                : <SoloRow key={idx} member={row.members[0]} idx={idx} />
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
